import { Button } from '@components/ui/button';
import { Checkbox } from '@components/ui/checkbox';
import { Input } from '@components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { cn } from '@utils/index';
import * as React from 'react';

interface MultiSelectOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface MultiSelectProps {
  /** Placed on the trigger, so callers can focus the field by id. */
  id?: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  maxCount?: number;
  /**
   * Singular noun used in the collapsed summary once more than `maxCount` items are
   * selected, e.g. `countNoun="program"` renders "4 programs selected". Defaults to the
   * generic "N selected".
   */
  countNoun?: string;
  icon?: React.ComponentType<{ className?: string }>;
  renderOption?: (option: MultiSelectOption) => React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  /** Show a search input and sort options alphabetically */
  searchable?: boolean;
  /** Placeholder text for the search input (when searchable is true) */
  searchPlaceholder?: string;
}

export function MultiSelect({
  id,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  className,
  contentClassName,
  disabled = false,
  maxCount = 4,
  countNoun,
  icon: Icon,
  renderOption,
  onOpenChange: onOpenChangeProp,
  searchable = false,
  searchPlaceholder = 'Search...',
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      // Clear the search when closing so the next open starts fresh
      setSearchQuery('');
    }
    onOpenChangeProp?.(value);
  };

  // Sort alphabetically and filter by search query when searchable is enabled
  const displayOptions = React.useMemo(() => {
    if (!searchable) return options;
    const sorted = [...options].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter(opt => opt.label.toLowerCase().includes(query));
  }, [options, searchable, searchQuery]);

  const handleSelect = (value: string) => {
    const newSelected = selected.includes(value)
      ? selected.filter(item => item !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  const handleClear = () => {
    onChange([]);
  };

  const displayValue = React.useMemo(() => {
    if (selected.length === 0) {
      return placeholder;
    }

    if (selected.length <= maxCount) {
      return selected
        .map(value => options.find(opt => opt.value === value)?.label)
        .filter(Boolean)
        .join(', ');
    }

    if (countNoun) {
      const noun = selected.length === 1 ? countNoun : `${countNoun}s`;
      return `${selected.length} ${noun} selected`;
    }
    return `${selected.length} selected`;
  }, [selected, options, placeholder, maxCount, countNoun]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn('w-full justify-between', className)}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {Icon && <Icon className="h-4 w-4 opacity-70 shrink-0" />}
            <span className="truncate flex-1 text-left font-normal">{displayValue}</span>
          </div>
          <div className="flex items-center shrink-0 ml-2">
            {selected.length > 0 && (
              <>
                <Cross2Icon
                  className="h-4 w-4 opacity-50 hover:opacity-100 mr-2"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                />
                <div className="w-px h-4 bg-border mr-2" />
              </>
            )}
            <ChevronDownIcon className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[var(--radix-popover-trigger-width)] p-2', contentClassName)}
        align="start"
      >
        {searchable && (
          <div className="mb-2">
            <Input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 text-sm"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          </div>
        )}
        <div
          role="listbox"
          aria-multiselectable="true"
          className="max-h-[300px] overflow-y-auto"
          onWheel={e => e.stopPropagation()}
        >
          {displayOptions.length === 0 ? (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">
              No results found
            </div>
          ) : (
            displayOptions.map(option => (
              <div
                key={option.value}
                role="option"
                aria-selected={selected.includes(option.value)}
                className="flex items-center space-x-2 px-3 py-1.5 hover:bg-accent hover:text-accent-foreground rounded-sm cursor-pointer transition-colors"
                onClick={() => handleSelect(option.value)}
              >
                <Checkbox
                  checked={selected.includes(option.value)}
                  onCheckedChange={() => handleSelect(option.value)}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
                <label
                  className="flex-1 cursor-pointer text-sm font-normal"
                  onClick={(e: React.MouseEvent) => e.preventDefault()}
                >
                  {renderOption ? (
                    renderOption(option)
                  ) : (
                    <>
                      {option.icon && <option.icon className="mr-2 h-4 w-4" />}
                      {option.label}
                    </>
                  )}
                </label>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Export the interface so it can be used elsewhere
